import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {AlertFallback} from '@rdcomponents/alert-fallback/alert-fallback';
import {DatePicker, Flex, Form, FormInstance, Input, Select, Space, Tag} from 'antd';
import dayjs from 'dayjs';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {spacer_l} from '../../../../css/var-export.scss';
import {PageCard} from '../../../../layout/page-card/page-card';
import {selectRatingContentStarsWithNoRating} from '../../../../layout/select/select-helper';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useFormRules} from '../../../../service/form/form-rules-hook';
import {ReviewFormErrorType} from '../../../../service/reviews/custom-reviews';
import {ReviewCustomCompanyType} from '../../../../service/reviews/reviews-custom-compamies';
import {ReviewFormSourcesType, useReviewFormSources} from '../../../../service/reviews/reviews-form-sources';
import {NoteFormType} from '../../../../service/reviews/reviews-notes';
import {ReviewBrandCompanyScopeCard} from '../review-brand-company-scope-card/review-brand-company-scope-card';
import {NoteUi} from '../review-list-page-content/new-review-notes/new-note/note-ui/note-ui';
import {NoteModal} from '../review-list-page-content/review-list/review/review-header/note-modal/note-modal';
import {ReviewTagsSelect} from '../review-tags-select/review-tags-select';

import {ReviewFormAttachments} from './review-form-attachments';
import {extractSource, getDisabledDate} from './review-form-helper';
import {ReviewEntityScopeEnum, ReviewFormFieldNamesEnum, ReviewFormFieldsType} from './review-form-type';
import * as styles from './review-form.scss';

type PropsType = {
    form: FormInstance<ReviewFormFieldsType>;
    editReview?: number;
    serverError: ReviewFormErrorType | null;
    initialCompany?: ReviewCustomCompanyType | null;
    initialValues?: ReviewFormFieldsType;
    authorInfo?: string | null;
};

export function CustomReviewFormFields(props: PropsType): JSX.Element {
    const {initialCompany, form, serverError, initialValues, authorInfo} = props;

    const {getLocalizedString} = useLocale();
    const {getPhoneNumberRule, requiredFieldRule, emailFieldRule, validUrlRule} = useFormRules();
    const {data: sources, isFetching: isSourcesInProgress} = useReviewFormSources();

    const noteList = Form.useWatch(ReviewFormFieldNamesEnum.notes, form) || [];
    const selectedBrandId = Form.useWatch(ReviewFormFieldNamesEnum.brand, form) || null;
    const [selectedSource, setSelectedSource] = useState<ReviewFormSourcesType | null>(null);
    const [isNotePopupOpen, setIsNotePopupOpen] = useState<boolean>(false);

    const formInitialValues = useMemo(
        () => ({
            [ReviewFormFieldNamesEnum.entityScope]: ReviewEntityScopeEnum.brand,
            ...initialValues,
        }),
        [initialValues]
    );

    const handleSourceChange = useCallback(
        (sourceId: ReviewFormSourcesType['id']) => {
            if (sources) {
                setSelectedSource(extractSource(sourceId, sources));
            }
        },
        [sources]
    );

    useEffect(() => {
        const hasInitialSource = initialValues && initialValues[ReviewFormFieldNamesEnum.source];

        if (!hasInitialSource && sources && sources[0]) {
            const [source] = sources;

            setSelectedSource(source);

            form.setFieldsValue({[ReviewFormFieldNamesEnum.source]: source.id});
        }
    }, [form, initialValues, sources, authorInfo]);

    function onNoteSubmitHandler(formValues: NoteFormType) {
        form.setFieldsValue({
            [ReviewFormFieldNamesEnum.notes]: [...noteList, {text: formValues.text, id: uuidv4()}],
        });
        setIsNotePopupOpen(false);
    }

    const hasNotes = noteList.length > 0;

    return (
        <Form
            className={styles.ReviewForm_formContainer}
            form={form}
            initialValues={formInitialValues}
            layout="vertical"
        >
            <PageCard title={<Locale stringKey="REVIEW__CUSTOM_FORM__GENERAL_SETTINGS" />}>
                <Flex className={styles.ReviewForm_itemContainer} vertical>
                    <Flex align="end" className={styles.ReviewForm_sourceRow} gap={spacer_l}>
                        <Form.Item
                            className={styles.ReviewForm_formSource}
                            label={<Locale stringKey="REVIEW__FORM__FIELD_SOURCE" />}
                            name={ReviewFormFieldNamesEnum.source}
                            required
                            rules={[requiredFieldRule]}
                        >
                            <Select<number>
                                disabled={isSourcesInProgress}
                                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                loading={isSourcesInProgress}
                                onChange={handleSourceChange}
                            >
                                {sources?.map((source) => (
                                    <Select.Option key={source.id} value={source.id}>
                                        {source.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {selectedSource && (
                            <img
                                alt=""
                                className={styles.ReviewForm_formSourceLogo}
                                src={selectedSource.logo}
                                title={selectedSource.name}
                            />
                        )}
                    </Flex>
                    <ReviewBrandCompanyScopeCard
                        fieldNames={{
                            entityScope: ReviewFormFieldNamesEnum.entityScope,
                            brand: ReviewFormFieldNamesEnum.brand,
                            company: ReviewFormFieldNamesEnum.company,
                        }}
                        form={form}
                        initialCompany={initialCompany}
                    />
                    <Form.Item
                        label={<Locale stringKey="REVIEW_GENERATOR__USED_PROMO_CODES_TABLE__REVIEW_LINK" />}
                        name={ReviewFormFieldNamesEnum.url}
                        rules={[validUrlRule]}
                    >
                        <Input placeholder="http://www.example.com" />
                    </Form.Item>
                    <Form.Item
                        initialValue={dayjs()}
                        label={<Locale stringKey="REVIEW__FORM__FIELD_CREATED_DATE" />}
                        name={ReviewFormFieldNamesEnum.createdAt}
                    >
                        <DatePicker
                            allowClear={false}
                            className={styles.ReviewForm_datePicker}
                            disabledDate={getDisabledDate}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                        />
                    </Form.Item>
                </Flex>
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEW__CUSTOM_FORM__AUTHOR" />}>
                <Flex className={styles.ReviewForm_itemContainer} vertical>
                    <Form.Item
                        label={<Locale stringKey="REVIEW__FORM__FIELD_AUTHOR" />}
                        name={ReviewFormFieldNamesEnum.author}
                    >
                        <Input placeholder={getLocalizedString('REVIEW__FORM__FIELD_AUTHOR__PLACEHOLDER')} />
                    </Form.Item>
                    <Form.Item
                        label={<Locale stringKey="REVIEW__FORM__FIELD_PHONE" />}
                        name={ReviewFormFieldNamesEnum.phone}
                        rules={[getPhoneNumberRule({required: false})]}
                    >
                        <Input placeholder={getLocalizedString('REVIEW__FORM__FIELD_PHONE__PLACEHOLDER')} />
                    </Form.Item>
                    <Form.Item
                        label={<Locale stringKey="REVIEWS_GENERATOR__CLIENTS__MANAGEMENT__FORM__INPUT__EMAIL__TITLE" />}
                        name={ReviewFormFieldNamesEnum.email}
                        rules={[emailFieldRule]}
                    >
                        <Input
                            placeholder={getLocalizedString(
                                'REVIEWS_GENERATOR__CLIENTS__MANAGEMENT__FORM__INPUT__EMAIL__PLACEHOLDER'
                            )}
                        />
                    </Form.Item>
                </Flex>
            </PageCard>
            <PageCard title={<Locale stringKey="REVIEW__CUSTOM_FORM__REVIEW_SETTING" />}>
                <Flex className={styles.ReviewForm_itemContainer} vertical>
                    <Form.Item
                        className={styles.ReviewForm_rating}
                        initialValue={0}
                        label={<Locale stringKey="REVIEW__FORM__FIELD_RATING" />}
                        name={ReviewFormFieldNamesEnum.rating}
                    >
                        <Select getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}>
                            {selectRatingContentStarsWithNoRating}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={<Locale stringKey="REVIEW__FORM__FIELD_CONTENT" />}
                        name={ReviewFormFieldNamesEnum.content}
                    >
                        <Input.TextArea placeholder={getLocalizedString('REVIEW__FORM__FIELD_CONTENT__PLACEHOLDER')} />
                    </Form.Item>
                    <Form.Item
                        label={<Locale stringKey="REVIEW__FORM__FIELD_ATTACHMENTS" />}
                        name={ReviewFormFieldNamesEnum.attachments}
                    >
                        <ReviewFormAttachments brandId={selectedBrandId} />
                    </Form.Item>
                    <Form.Item
                        label={<Locale stringKey="REVIEWS__FILTER__LABEL__TAGS" />}
                        name={ReviewFormFieldNamesEnum.tags}
                    >
                        <ReviewTagsSelect />
                    </Form.Item>
                    <div className={styles.ReviewForm_notesSection}>
                        <Form.Item
                            className={styles.ReviewForm_notesFormItem}
                            label={<Locale stringKey="REVIEW__CUSTOM_FORM__REVIEW_NOTES" />}
                            name={ReviewFormFieldNamesEnum.notes}
                        />
                        <div className={styles.ReviewForm_notesContent}>
                            {hasNotes && (
                                <Flex className={styles.ReviewForm_notesList} gap={spacer_l} vertical>
                                    {noteList.map((note) => (
                                        <NoteUi
                                            createdAt={note.createdAt}
                                            handleDeleteClick={() =>
                                                form.setFieldValue(
                                                    ReviewFormFieldNamesEnum.notes,
                                                    noteList.filter((filteredNote) => filteredNote.id !== note.id)
                                                )
                                            }
                                            handleEdit={(id, text) => {
                                                form.setFieldValue(
                                                    ReviewFormFieldNamesEnum.notes,
                                                    noteList.map((filteredNote) =>
                                                        filteredNote.id === id ? {...filteredNote, text} : filteredNote
                                                    )
                                                );
                                            }}
                                            key={note.id}
                                            noteId={note.id}
                                            noteText={note.text}
                                            updatedAt={note.updatedAt}
                                            userEmail={note.user?.email}
                                        />
                                    ))}
                                </Flex>
                            )}
                            <Tag className={styles.ReviewForm_addNoteButton} onClick={() => setIsNotePopupOpen(true)}>
                                <Space size={4}>
                                    <FontAwesomeIcon icon={faPlus} />
                                    <Locale stringKey="REVIEW__DROPDOWN_MENU__ADD_NOTE" />
                                </Space>
                            </Tag>
                        </div>
                    </div>
                    {serverError && <AlertFallback message={serverError?.non_field_errors} />}
                    {isNotePopupOpen && (
                        <NoteModal
                            isLoading={false}
                            onCancel={setIsNotePopupOpen}
                            onSubmit={onNoteSubmitHandler}
                            title={<Locale stringKey="REVIEWS__NOTE_MODAL__TITLE" />}
                        />
                    )}
                </Flex>
            </PageCard>
        </Form>
    );
}
